import type { ReactNode } from 'react';
import type { SociedadResumen } from '../tipos';

export function Cifra({
  etiqueta,
  valor,
  pie,
  tono,
}: {
  etiqueta: string;
  valor: ReactNode;
  pie?: ReactNode;
  tono?: 'mal' | 'ojo';
}): JSX.Element {
  return (
    <div className="tarjeta cifra">
      <div className="etiqueta">{etiqueta}</div>
      <div className={`valor ${tono ?? ''}`}>{valor}</div>
      {pie ? <div className="pie">{pie}</div> : null}
    </div>
  );
}

/**
 * Distintivo de estado.
 *
 * Siempre color + icono + texto. El color solo no dice nada a quien no lo
 * distingue, y desaparece del todo en una impresión en blanco y negro o en
 * una pantalla de taller con el brillo a tope.
 */
export function Dist({
  tono,
  icono,
  children,
}: {
  tono: 'critico' | 'serio' | 'aviso' | 'bien' | 'neutro';
  icono?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <span className={`dist ${tono}`}>
      {icono ? <span className="icono" aria-hidden="true">{icono}</span> : null}
      {children}
    </span>
  );
}

const ACCIONES: Record<string, { texto: string; tono: 'critico' | 'serio' | 'aviso' | 'bien' | 'neutro'; icono: string }> = {
  purchase: { texto: 'Comprar', tono: 'neutro', icono: '↓' },
  produce: { texto: 'Fabricar', tono: 'neutro', icono: '⚙' },
  expedite: { texto: 'Adelantar', tono: 'serio', icono: '«' },
  defer: { texto: 'Retrasar', tono: 'aviso', icono: '»' },
  cancel: { texto: 'Cancelar', tono: 'critico', icono: '×' },
};

export function Accion({ accion }: { accion: string }): JSX.Element {
  const a = ACCIONES[accion] ?? { texto: accion, tono: 'neutro' as const, icono: '' };
  return <Dist tono={a.tono} icono={a.icono}>{a.texto}</Dist>;
}

export function Gravedad({ severity }: { severity: string }): JSX.Element {
  if (severity === 'blocker') return <Dist tono="critico" icono="■">Bloquea</Dist>;
  if (severity === 'warning') return <Dist tono="aviso" icono="▲">Aviso</Dist>;
  return <Dist tono="neutro" icono="●">Informa</Dist>;
}

/**
 * Con qué sociedad se está trabajando, con su CIF.
 *
 * Va en la barra superior de todas las pantallas. Un plan de la sociedad
 * equivocada se parece muchísimo a uno bueno: la única defensa es tenerlo
 * delante sin ir a buscarlo. Y si la sociedad no tiene CIF, se dice aquí, no
 * en un log que no abre nadie.
 */
export function Matricula({ sociedad }: { sociedad: SociedadResumen | null }): JSX.Element | null {
  if (!sociedad) return null;
  const sinCif = sociedad.cif === null;
  return (
    <span className={`sociedad ${sinCif ? 'sin-cif' : ''}`} title={
      sinCif
        ? 'Esta sociedad no tiene CIF: sus peticiones a SAP salen sin la cabecera X-Ecinsa-CIF'
        : `CIF ${sociedad.cif} · ${sociedad.cifSource === 'sap' ? 'leído del propio SAP' : 'tecleado en el panel'}`
    }>
      <strong>{sociedad.companyDb}</strong>
      <span className="cif">{sinCif ? '⚠ sin CIF' : sociedad.cif}</span>
    </span>
  );
}

export function Avisos({ titulo, items, error }: { titulo: string; items: string[]; error?: boolean }): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className={`aviso-caja ${error ? 'error' : ''}`}>
      <div className="titulo">{titulo}</div>
      <ul>
        {items.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
    </div>
  );
}

export function Vacio({ children }: { children: ReactNode }): JSX.Element {
  return <div className="vacio">{children}</div>;
}
